import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import {
  DEFAULT_DATE_MASK,
  isCompilableFieldPattern,
  TAXONOMY_DATE_MASK_OPTIONS,
  TAXONOMY_FIELD_TYPE_LABELS,
  TAXONOMY_LEVEL_OPTIONS,
  TAXONOMY_SELECTABLE_FIELD_TYPES,
  TAXONOMY_TEXT_TRANSFORM_OPTIONS,
  taxonomyDateMaskExample,
  type TaxonomyField,
  type TaxonomyFieldGroup,
  type TaxonomyListRef,
  type TaxonomyFieldType,
  type TaxonomyTextTransform,
} from '@/entities/taxonomy';
import { FormSheet } from '@/shared/ui/FormSheet';
import { ListBindingFields } from '../ListBinding/ListBindingFields';
import { useListBinding } from '../ListBinding/useListBinding';

export interface EditFieldModalProps {
  open: boolean;
  field: TaxonomyField;
  /** Parent's name when `field` is a subfield — names what its section follows. */
  parentName?: string;
  /** The schema is shared across the organization, so only organization lists can be bound. */
  orgWide?: boolean;
  onClose: () => void;
  onSave: (patch: {
    name: string;
    group: TaxonomyFieldGroup;
    type: TaxonomyFieldType;
    required: boolean;
    format?: string | null;
    level?: string | null;
    description?: string | null;
    visible?: boolean;
    unique?: boolean;
    defaultValue?: string | null;
    validationPattern?: string | null;
    transform?: TaxonomyTextTransform | null;
    /** Present only when the binding changed; `undefined` as a value unbinds. */
    listRef?: TaxonomyListRef;
  }) => void;
}

// eslint-disable-next-line i18next/no-literal-string -- the stored `level` value, not copy
const DEFAULT_LEVEL = 'all';

/** Edits an existing field's title, type, and required flag in one place. */
export function EditFieldModal({
  open,
  field,
  parentName,
  orgWide,
  onClose,
  onSave,
}: EditFieldModalProps) {
  const { t } = useTranslation('knowledge');
  const nameId = useId();
  const formatId = useId();
  const maskListId = useId();
  const typeId = useId();
  const descriptionId = useId();
  const defaultValueId = useId();
  const patternId = useId();
  const [name, setName] = useState(field.name);
  const [group, setGroup] = useState<TaxonomyFieldGroup>(field.group);
  const [type, setType] = useState<TaxonomyFieldType>(field.type);
  const [required, setRequired] = useState(field.required);
  const [format, setFormat] = useState(field.format ?? '');
  const [level, setLevel] = useState(field.level ?? DEFAULT_LEVEL);
  const [description, setDescription] = useState(field.description ?? '');
  const [visible, setVisible] = useState(field.visible ?? true);
  const [unique, setUnique] = useState(field.unique ?? false);
  const [defaultValue, setDefaultValue] = useState(field.defaultValue ?? '');
  const [validationPattern, setValidationPattern] = useState(field.validationPattern ?? '');
  const [transform, setTransform] = useState<TaxonomyTextTransform>(field.transform ?? 'none');
  const binding = useListBinding({ open, type, initialListRef: field.listRef, orgWide });

  useEffect(() => {
    if (open) {
      setName(field.name);
      setGroup(field.group);
      setType(field.type);
      setRequired(field.required);
      setFormat(field.format ?? '');
      setLevel(field.level ?? DEFAULT_LEVEL);
      setDescription(field.description ?? '');
      setVisible(field.visible ?? true);
      setUnique(field.unique ?? false);
      setDefaultValue(field.defaultValue ?? '');
      setValidationPattern(field.validationPattern ?? '');
      setTransform(field.transform ?? 'none');
    }
  }, [open, field]);

  const trimmed = name.trim();
  const maskExample = taxonomyDateMaskExample(format);
  // Gate on the API's own storability rule, not on `new RegExp`. The legacy
  // parse accepts things the API refuses (`[[:alpha:]]+`, `x\z`, stray
  // escapes), so checking it here let someone save and collect a 400 for a
  // pattern this form had just called valid.
  const trimmedPattern = validationPattern.trim();
  const patternError =
    trimmedPattern && !isCompilableFieldPattern(trimmedPattern)
      ? t('taxonomy.editFieldModal.notValidRegularExpression')
      : null;
  const canSave = trimmed.length > 0 && !patternError && !binding.blocksSave;

  const handleSave = () => {
    if (!canSave) return;
    const change = binding.change();
    onSave({
      name: trimmed,
      group,
      type,
      required,
      // Only a date has a mask. Writing `null` on every other type also CLEANS
      // UP values stranded by an earlier type change — without this the hidden
      // input kept re-persisting a stale format the user could no longer see.
      format: type === 'date' ? format.trim() || null : null,
      level,
      description: description.trim() || null,
      visible,
      unique,
      defaultValue: defaultValue.trim() || null,
      validationPattern: validationPattern.trim() || null,
      transform,
      ...(change.kind === 'bind' ? { listRef: change.listRef } : {}),
      ...(change.kind === 'unbind' ? { listRef: undefined } : {}),
    });
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.editFieldModal.editField')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={nameId}>
            {t('taxonomy.editFieldModal.fieldName')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('taxonomy.editFieldModal.eGObjective')}
            maxLength={100}
            className="bg-input"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={descriptionId}>{t('taxonomy.editFieldModal.descriptionOptional')}</Label>
          <Input
            id={descriptionId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('taxonomy.editFieldModal.whatFieldIsFor')}
            maxLength={500}
            className="bg-input"
          />
          <span className="text-xs text-muted-foreground">
            {t('taxonomy.editFieldModal.documentsFieldDataDictionary')}
          </span>
        </div>

        {/* A subfield's section is its PARENT's — offering the choice let it be
            moved to the other group, which tore it away from the field it
            qualifies (the two tables are separate, so it showed up as an orphan
            column). `AddFieldModal` already locks this on create; editing has to
            agree. Change the parent to move the pair. */}
        <div className="flex flex-col gap-1.5">
          <Label id={`${nameId}-section`}>{t('taxonomy.editFieldModal.section')}</Label>
          {field.parentId ? (
            <>
              <div className="rounded-lg border border-border-light bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {group === 'media'
                  ? t('taxonomy.editFieldModal.mediaField')
                  : t('taxonomy.editFieldModal.contentField')}
              </div>
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.editFieldModal.subfieldStaysSameSection', {
                  parent: parentName
                    ? `“${parentName}”`
                    : t('taxonomy.editFieldModal.followsParentField'),
                })}
              </span>
            </>
          ) : (
            <ToggleGroup
              aria-labelledby={`${nameId}-section`}
              type="single"
              value={group}
              onValueChange={(v) => v && setGroup(v as TaxonomyFieldGroup)}
              variant="outline"
              size="sm"
              className="grid w-full grid-cols-2"
            >
              {/* eslint-disable-next-line i18next/no-literal-string -- `value` is the TaxonomyFieldGroup wire enum */}
              <ToggleGroupItem value="media">
                {t('taxonomy.editFieldModal.mediaField')}
              </ToggleGroupItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- `value` is the TaxonomyFieldGroup wire enum */}
              <ToggleGroupItem value="content">
                {t('taxonomy.editFieldModal.contentField')}
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.editFieldModal.hierarchyLevel')}</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAXONOMY_LEVEL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {t('taxonomy.editFieldModal.whereFieldSitsHierarchy')}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={typeId}>{t('taxonomy.editFieldModal.type')}</Label>
          <Select
            value={type}
            onValueChange={(v) => {
              const nextType = v as TaxonomyFieldType;
              setType(nextType);
              if (nextType !== 'date') setFormat('');
            }}
          >
            <SelectTrigger id={typeId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAXONOMY_SELECTABLE_FIELD_TYPES.map((fieldType) => (
                <SelectItem key={fieldType} value={fieldType}>
                  {TAXONOMY_FIELD_TYPE_LABELS[fieldType]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {type === 'dropdown' && <ListBindingFields binding={binding} />}

        {/* A format left behind by an earlier type change. Surfaced rather than
            hidden: the value still rendered as an uppercase pill in the naming
            preview, so silently concealing it left the user staring at a badge
            with no way to reach it. Saving clears it either way — this just
            makes that visible and immediate. */}
        {type !== 'date' && format.trim() && (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border-light bg-muted/40 p-3">
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">
                {t('taxonomy.editFieldModal.leftoverDateFormat', { format: format.trim() })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.editFieldModal.formatsOnlyApplyDateFields', {
                  type: TAXONOMY_FIELD_TYPE_LABELS[type],
                })}
              </span>
            </span>
            <Button variant="outline" size="sm" onClick={() => setFormat('')}>
              {t('taxonomy.editFieldModal.clear')}
            </Button>
          </div>
        )}

        {/* Date only: `format` is the mask the date picker WRITES, not a free
            annotation. Offered on every type it let people put arbitrary text
            on a free-text field, which then rendered as an uppercase pill in
            the naming preview (a field formatted "gene" showed up as GENE). */}
        {type === 'date' && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={formatId}>{t('taxonomy.editFieldModal.dateFormat')}</Label>
            <Input
              id={formatId}
              list={maskListId}
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              placeholder={DEFAULT_DATE_MASK}
              maxLength={40}
              className="bg-input font-mono"
            />
            <datalist id={maskListId}>
              {TAXONOMY_DATE_MASK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.example}
                </option>
              ))}
            </datalist>
            <span className="text-xs text-muted-foreground">
              {maskExample
                ? t('taxonomy.editFieldModal.writesDatesAs', { example: maskExample })
                : t('taxonomy.editFieldModal.leaveBlankFor', { mask: DEFAULT_DATE_MASK })}
            </span>
          </div>
        )}

        {/* Governance rules — enforced by the naming engine at generation time. */}
        <div className="flex flex-col gap-4 border-t border-border-light pt-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('taxonomy.editFieldModal.governance')}
          </span>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={defaultValueId}>
              {t('taxonomy.editFieldModal.defaultValueOptional')}
            </Label>
            <Input
              id={defaultValueId}
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              placeholder={t('taxonomy.editFieldModal.preselectedWhenRecordCreated')}
              maxLength={255}
              className="bg-input"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={patternId}>
              {t('taxonomy.editFieldModal.validationPatternOptional')}
            </Label>
            <Input
              id={patternId}
              value={validationPattern}
              onChange={(e) => setValidationPattern(e.target.value)}
              placeholder={t('taxonomy.editFieldModal.regexEG')}
              maxLength={500}
              className="bg-input font-mono"
            />
            <span
              className={`text-xs ${patternError ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {patternError ?? t('taxonomy.editFieldModal.valueMustMatchFull')}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('taxonomy.editFieldModal.textTransformation')}</Label>
            <Select
              value={transform}
              onValueChange={(v) => setTransform(v as TaxonomyTextTransform)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TAXONOMY_TEXT_TRANSFORM_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {t('taxonomy.editFieldModal.appliedFieldOutputGeneratedName')}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border-light pt-4">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{t('taxonomy.editFieldModal.required')}</span>
            <span className="text-xs text-muted-foreground">
              {t('taxonomy.editFieldModal.generatedNamesMustIncludeValue')}
            </span>
          </div>
          <Switch
            checked={required}
            onCheckedChange={setRequired}
            aria-label={t('taxonomy.editFieldModal.requiredField')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{t('taxonomy.editFieldModal.visible')}</span>
            <span className="text-xs text-muted-foreground">
              {t('taxonomy.editFieldModal.shownDuringSubmissionHideAutoGenerated')}
            </span>
          </div>
          <Switch
            checked={visible}
            onCheckedChange={setVisible}
            aria-label={t('taxonomy.editFieldModal.visibleField')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{t('taxonomy.editFieldModal.unique')}</span>
            <span className="text-xs text-muted-foreground">
              {t('taxonomy.editFieldModal.eachRecordMustHaveDifferentValue')}
            </span>
          </div>
          <Switch
            checked={unique}
            onCheckedChange={setUnique}
            aria-label={t('taxonomy.editFieldModal.uniqueField')}
          />
        </div>
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: t('taxonomy.editFieldModal.save'),
          onClick: handleSave,
          disabled: !canSave,
        }}
        secondary={{ label: t('taxonomy.editFieldModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

EditFieldModal.displayName = 'EditFieldModal';
